import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { STORAGE_KEYS } from '@/config';
import { useT } from '@/i18n/LocaleProvider';

import type { AccountResponse } from './api';
import { createAuthApiClient } from './api';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

type AuthState = {
  token: string | null;
  account: AccountResponse | null;
  status: AuthStatus;
};

export type AuthContextValue = AuthState & {
  isAuthenticated: boolean;
  login(email: string, password: string): Promise<AccountResponse>;
  register(email: string, password: string, confirmPassword: string): Promise<AccountResponse>;
  changePassword(currentPassword: string, newPassword: string): Promise<AccountResponse>;
  logout(): void;
  onUnauthorized(): void;
};

const AuthContext = createContext<AuthContextValue | null>(null);
const authClient = createAuthApiClient();

export function AuthProvider({ children }: { children: ReactNode }) {
  const t = useT();
  const [auth, setAuth] = useState<AuthState>(() => {
    const stored = loadStoredAuth();
    return { ...stored, status: stored.token ? 'loading' : 'anonymous' };
  });

  // Capture initial token for mount-time validation; ref is stable across re-renders.
  const initialTokenRef = useRef(auth.token);

  useEffect(() => {
    const token = initialTokenRef.current;

    if (!token) {
      return;
    }

    let cancelled = false;

    authClient.me(token).then(
      (me) => {
        if (cancelled) return;

        const account: AccountResponse = {
          id: me.id,
          email: me.email,
          role_id: me.role_id,
          extra_scopes: me.scopes,
        };

        writeStoredAuth(token, account);
        setAuth((prev) => ({ ...prev, account, status: 'authenticated' }));
      },
      () => {
        if (cancelled) return;
        clearStoredAuth();
        setAuth({ token: null, account: null, status: 'anonymous' });
      },
    );

    return () => {
      cancelled = true;
    };
  }, []);

  const persistAuth = useCallback((token: string, account: AccountResponse) => {
    writeStoredAuth(token, account);
    setAuth({ token, account, status: 'authenticated' });
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await authClient.login({ email, password });
      persistAuth(result.token, result.account);

      return result.account;
    },
    [persistAuth],
  );

  const register = useCallback(
    async (email: string, password: string, confirmPassword: string) => {
      await authClient.register({ email, password, confirm_password: confirmPassword });

      return login(email, password);
    },
    [login],
  );

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      if (!auth.token) {
        throw new Error(t('error_login_required'));
      }

      const account = await authClient.changePassword(auth.token, {
        current_password: currentPassword,
        new_password: newPassword,
      });

      persistAuth(auth.token, account);

      return account;
    },
    [auth.token, persistAuth, t],
  );

  const logout = useCallback(() => {
    clearStoredAuth();
    setAuth({ token: null, account: null, status: 'anonymous' });
  }, []);

  const onUnauthorized = useCallback(() => {
    logout();
  }, [logout]);

  const value = useMemo<AuthContextValue>(
    () => ({
      token: auth.token,
      account: auth.account,
      status: auth.status,
      isAuthenticated: auth.status === 'authenticated',
      login,
      register,
      changePassword,
      logout,
      onUnauthorized,
    }),
    [auth, login, register, changePassword, logout, onUnauthorized],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }

  return context;
}

function loadStoredAuth(): { token: string | null; account: AccountResponse | null } {
  if (typeof localStorage === 'undefined') {
    return { token: null, account: null };
  }

  try {
    const token = localStorage.getItem(STORAGE_KEYS.authToken);
    const rawAccount = localStorage.getItem(STORAGE_KEYS.authAccount);
    const account = rawAccount ? (JSON.parse(rawAccount) as AccountResponse) : null;

    return { token, account };
  } catch {
    return { token: null, account: null };
  }
}

function writeStoredAuth(token: string, account: AccountResponse) {
  if (typeof localStorage === 'undefined') {
    return;
  }

  localStorage.setItem(STORAGE_KEYS.authToken, token);
  localStorage.setItem(STORAGE_KEYS.authAccount, JSON.stringify(account));
}

function clearStoredAuth() {
  if (typeof localStorage === 'undefined') {
    return;
  }

  localStorage.removeItem(STORAGE_KEYS.authToken);
  localStorage.removeItem(STORAGE_KEYS.authAccount);
}
